import React from "react";
import { Helmet } from "react-helmet-async";

const BASE = "https://4starsdanceaerialstudio.pl";
const BRAND = "4STARS Dance & Aerial Studio";
const DEFAULT_IMAGE = `${BASE}/home_slide1.jpg`;

export default function Seo({
  title,
  description,
  path = "",
  image,
  noindex = false,
  type = "website",
  children,
}) {
  const fullTitle = title ? `${title} | ${BRAND}` : BRAND;
  const url = `${BASE}${path}`;
  const img = image
    ? image.startsWith("http")
      ? image
      : `${BASE}${image.startsWith("/") ? "" : "/"}${image}`
    : DEFAULT_IMAGE;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description ? <meta name="description" content={description} /> : null}

      <link rel="canonical" href={url} />

      {noindex ? <meta name="robots" content="noindex,follow" /> : null}

      <meta property="og:site_name" content={BRAND} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content="pl_PL" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description ? <meta name="twitter:description" content={description} /> : null}
      <meta name="twitter:image" content={img} />

      {children}
    </Helmet>
  );
}

export function SeoLocation({ location }) {
  if (!location) return null;

  const city = location.city || "";
  const name = location.name || "";

  const title =
    location.metaTitle ||
    `Zajęcia dla dzieci - ${name}${city ? `, ${city}` : ""}`;

  const description =
    location.metaDescription ||
    buildDescription(location);

  const schema = {
    "@context": "https://schema.org",
    "@type": "SportsActivityLocation",
    name: `${BRAND} — ${name}`,
    url: `${BASE}/${location.slug}`,
    telephone: "+48505376552",
    address: {
      "@type": "PostalAddress",
      streetAddress: location.address || "",
      addressLocality: city,
      addressCountry: "PL",
    },
    ...(location.photo ? { image: `${BASE}${location.photo}` } : {}),
    ...(location.geo?.lat && location.geo?.lng
      ? {
          geo: {
            "@type": "GeoCoordinates",
            latitude: location.geo.lat,
            longitude: location.geo.lng,
          },
        }
      : {}),
    sameAs: [
      "https://www.instagram.com/4starsdanceaerialstudio",
      "https://www.facebook.com/4stars.centrum.ruchu",
      "https://www.tiktok.com/@4starsdanceaerialstudio",
      "https://www.youtube.com/@4STARSDanceAerialStudio",
    ],
  };

  return (
    <Seo
      title={title}
      description={description}
      path={`/${location.slug}`}
      image={location.photo}
    >
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Seo>
  );
}

function buildDescription(location) {
  const disciplines = (location.classes || [])
    .map((c) => c?.name)
    .filter(Boolean)
    .slice(0, 3)
    .join(", ");

  const where = location.city
    ? `${location.city}, ${location.address || ""}`.replace(/,\s*$/, "")
    : location.address || "";

  const what = disciplines || "Taniec i akrobatyka powietrzna dla dzieci";

  return `${what}. ${where}. Sprawdź grafik zajęć i zapisz dziecko - zajęcia dla dzieci.`
    .replace(/\s+/g, " ")
    .slice(0, 155);
}
